import { useNavigate } from "react-router-dom";
import { ArrowDownLeft, ArrowUpRight, ClipboardList, FileQuestion, ListChecks, Package, ReplaceAll, RotateCcw, Trash2, UserCog, Wrench } from "lucide-react";

/**
 * Aktionen-Hub -- Monteur-First gruppiert:
 *   1. Täglich -- Die 90%-Flows (Entnahme/Umlagerung/Ausleihe/Rückgabe)
 *   2. Häufig  -- Wareneingang, Korrektur, Pickliste
 *   3. Ausnahme -- Entsorgung, Gegenstand aufnehmen, Inventur (kleiner)
 */
// #135 Befund 9: keine zweite Ausgabe der großen Start-Kacheln -- der Hub ist
// der kompakte Gesamtkatalog, die Tageskacheln leben nur auf dem Start.
const DAILY = [
    { to: "/flows/entnahme", label: "Entnahme", icon: ArrowUpRight, color: "text-primary", hint: "Material verbrauchen" },
    { to: "/flows/umlagerung", label: "Umlagerung", icon: ReplaceAll, color: "text-info", hint: "Fach wechseln / einlagern" },
    { to: "/flows/ausleihe", label: "Ausleihe", icon: UserCog, color: "text-secondary", hint: "Gerät an Person" },
    { to: "/flows/rueckgabe", label: "Rückgabe", icon: RotateCcw, color: "text-warning", hint: "Gerät zurücknehmen" },
];

const FREQUENT = [
    { to: "/flows/eingang", label: "Wareneingang", icon: ArrowDownLeft, hint: "Lieferung annehmen" },
    { to: "/flows/korrektur", label: "Korrektur", icon: Wrench, hint: "Menge richtigstellen" },
    { to: "/flows/pickliste", label: "Pickliste", icon: ClipboardList, hint: "Kommissionieren" },
    { to: "/artikel", label: "Artikel suchen", icon: Package, hint: "Wo liegt was?" },
];

const RARE = [
    { to: "/flows/entsorgung", label: "Entsorgung", icon: Trash2, tone: "destructive" },
    { to: "/flows/unknown-item", label: "Gegenstand aufnehmen", icon: FileQuestion, tone: "info" },
    { to: "/flows/inventory-count", label: "Inventur-Zählung", icon: ListChecks, tone: "muted" },
];

function toneClass(t) {
    if (t === "destructive") return "text-destructive";
    if (t === "info") return "text-info";
    return "text-muted-foreground";
}

export default function AktionenHub() {
    const navigate = useNavigate();
    return (
        <section data-testid="aktionen-hub" className="flex flex-col gap-6">
            <div>
                <div className="text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Floor Mode
                </div>
                <h1 className="text-2xl font-bold tracking-tight text-foreground">Aktionen</h1>
            </div>

            {/* DAILY -- kompakte Listenzeilen (große Kacheln nur auf dem Start) */}
            <div>
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Täglich
                </div>
                <div className="flex flex-col gap-2" data-testid="aktionen-daily">
                    {DAILY.map((a) => {
                        const Icon = a.icon;
                        return (
                            <button
                                key={a.to}
                                type="button"
                                onClick={() => navigate(a.to)}
                                className="pw-tap flex items-center gap-3 rounded-xl border border-border bg-card/40 px-4 py-3 text-left hover:border-primary/40"
                                data-testid={`daily-${a.label.toLowerCase()}`}
                            >
                                <Icon className={`h-5 w-5 ${a.color}`} strokeWidth={2.4} />
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-foreground">
                                        {a.label}
                                    </div>
                                    <div className="text-xs text-muted-foreground">
                                        {a.hint}
                                    </div>
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* FREQUENT -- breite Listen-Kacheln */}
            <div>
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Häufig
                </div>
                <div className="flex flex-col gap-2" data-testid="aktionen-frequent">
                    {FREQUENT.map((a) => {
                        const Icon = a.icon;
                        return (
                            <button
                                key={a.to}
                                type="button"
                                onClick={() => navigate(a.to)}
                                className="pw-tap flex items-center gap-3 rounded-xl border border-border bg-card/40 px-4 py-3 text-left hover:border-primary/40"
                                data-testid={`freq-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <Icon className="h-5 w-5 text-primary" />
                                <div className="flex-1">
                                    <div className="text-sm font-medium text-foreground">
                                        {a.label}
                                    </div>
                                    {a.hint ? (
                                        <div className="text-xs text-muted-foreground">
                                            {a.hint}
                                        </div>
                                    ) : null}
                                </div>
                            </button>
                        );
                    })}
                </div>
            </div>

            {/* RARE -- kleinere Tiles */}
            <div>
                <div className="mb-2 text-xs uppercase tracking-[0.2em] text-muted-foreground">
                    Ausnahmen
                </div>
                <div className="grid grid-cols-3 gap-2" data-testid="aktionen-rare">
                    {RARE.map((a) => {
                        const Icon = a.icon;
                        return (
                            <button
                                key={a.to}
                                type="button"
                                onClick={() => navigate(a.to)}
                                className="pw-tap flex flex-col items-center gap-1 rounded-lg border border-border/60 bg-card/30 px-2 py-3 text-center hover:border-primary/40"
                                data-testid={`rare-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
                            >
                                <Icon className={`h-4 w-4 ${toneClass(a.tone)}`} />
                                <span className="text-xs leading-tight text-foreground">
                                    {a.label}
                                </span>
                            </button>
                        );
                    })}
                </div>
            </div>
        </section>
    );
}
